import { useCallback, useEffect, useState } from "react";
import { Table, Tabs, message } from "antd";
import DataKitApi from "../../../../api/DataKitApi";
import { DataKitDto, FinalMenuDto, MenuUsageDto } from "../../../../domain/DataKitDto";
import "./DataKitMenusTab.scss";

const PAGE_SIZE = 50;

interface Props {
  kit: DataKitDto;
  menus: MenuUsageDto[];
}

export function DataKitMenusTab({ kit, menus }: Props) {
  const [rows, setRows] = useState<FinalMenuDto[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const loadFinal = useCallback(
    (nextPage: number) => {
      if (!kit.id) return;
      setLoading(true);
      DataKitApi.listFinalMenus(kit.id, PAGE_SIZE, (nextPage - 1) * PAGE_SIZE)
        .then((res) => {
          setRows(res.data.rows || []);
          setTotal(res.data.total || 0);
        })
        .catch(() => message.error("Could not load final menus"))
        .finally(() => setLoading(false));
    },
    [kit.id]
  );

  useEffect(() => {
    loadFinal(page);
  }, [loadFinal, page]);

  const templateColumns = [
    {
      title: "Menu",
      dataIndex: "menuLabel",
      key: "menuLabel",
      sorter: (a: MenuUsageDto, b: MenuUsageDto) =>
        (a.menuLabel || "").localeCompare(b.menuLabel || "", undefined, { sensitivity: "base" }),
      showSorterTooltip: false,
      render: (label?: string | null) => label || "Unlabelled",
    },
    {
      title: "Designs",
      dataIndex: "designCount",
      key: "designCount",
      width: 300,
      render: (count: number) => count ?? 0,
    },
  ];

  const finalColumns = [
    {
      title: "Menu",
      key: "design",
      render: (_: unknown, row: FinalMenuDto) => (
        <div className="data-kit-final-name-cell">
          <div className="data-kit-final-name">{row.designName}</div>
          {row.menuLabel && <div className="data-kit-final-label">{row.menuLabel}</div>}
        </div>
      ),
    },
    {
      title: "Location",
      key: "location",
      width: 300,
      render: (_: unknown, row: FinalMenuDto) =>
        row.locationName || <span className="data-kit-final-empty">Account level</span>,
    },
    {
      title: "Items from this kit",
      dataIndex: "itemCount",
      key: "itemCount",
      width: 180,
      render: (count: number) => count ?? 0,
    },
  ];

  return (
    <div className="data-kit-menus-tab">
      <Tabs
        className="data-kit-menus-subtabs"
        items={[
          {
            key: "templates",
            label: `Brand Templates - ${menus.length}`,
            children: (
              <>
                <p className="data-kit-menus-hint">
                  One row per menu label, counting the designs that use it.
                </p>
                <Table
                  className="data-kit-menus-table"
                  dataSource={menus}
                  columns={templateColumns}
                  rowKey={(row) => row.menuLabel || "__unlabelled__"}
                  pagination={false}
                  locale={{ emptyText: "No menus use this kit yet" }}
                />
              </>
            ),
          },
          {
            key: "final",
            label: `Final Menus - ${total}`,
            children: (
              <>
                <p className="data-kit-menus-hint">
                  Every actual menu using this kit, one row per design.
                </p>
                <Table
                  className="data-kit-menus-table"
                  dataSource={rows}
                  columns={finalColumns}
                  rowKey="designId"
                  loading={loading}
                  pagination={
                    total > PAGE_SIZE
                      ? {
                          current: page,
                          pageSize: PAGE_SIZE,
                          total,
                          onChange: setPage,
                          showSizeChanger: false,
                        }
                      : false
                  }
                  locale={{ emptyText: "No menus use this kit yet" }}
                />
              </>
            ),
          },
        ]}
      />
    </div>
  );
}
